import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [organization, setOrganization] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      const userData = res.data.user;
      setUser(userData);
      setOrganization(res.data.organization);

      if (userData.scope === "tenant") {
        const branchRes = await api.get("/branches");
        let allBranches = branchRes.data.branches;

        // Staff with an assigned branch: only show that branch in the sidebar
        if (userData.role === "staff" && userData.branchId) {
          const assigned = allBranches.filter((b) => b.id === userData.branchId);
          // Fall back to all branches if the assigned one isn't found (safety net)
          allBranches = assigned.length > 0 ? assigned : allBranches;
        }

        setBranches(allBranches);

        // Auto-select: prefer saved branch (if still in the visible list), else first
        const saved = localStorage.getItem("currentBranch");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            // Only restore if the saved branch is in the (possibly filtered) list
            const stillVisible = allBranches.find((b) => b.id === parsed.id);
            setCurrentBranch(stillVisible || allBranches[0] || null);
          } catch {
            setCurrentBranch(allBranches[0] || null);
          }
        } else if (allBranches.length > 0) {
          setCurrentBranch(allBranches[0]);
        }
      }
    } catch {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("token", newToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (data) => {
    const res = await api.post("/auth/signup", data);
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("token", newToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = useCallback(() => {
    const slug = organization?.slug || localStorage.getItem("org_slug");
    localStorage.removeItem("token");
    localStorage.removeItem("currentBranch");
    localStorage.removeItem("org_slug");
    delete api.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setOrganization(null);
    setBranches([]);
    setCurrentBranch(null);
    return slug ? `/org/${slug}/login` : "/login";
  }, [organization]);

  const switchBranch = (branch) => {
    setCurrentBranch(branch);
    if (branch) {
      localStorage.setItem("currentBranch", JSON.stringify(branch));
    } else {
      localStorage.removeItem("currentBranch");
    }
  };

  /**
   * Check if the current user has a given permission.
   * - Owners / super_admin always return true
   * - Staff must have the specific key in their permissions array
   * - "all" wildcard also grants everything
   */
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === "owner" || user.role === "super_admin") return true;
    const perms = user.permissions || [];
    return perms.includes("all") || perms.includes(permission);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, signup, logout,
      currentBranch, branches, switchBranch, setBranches,
      organization, setOrganization, fetchUser,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
